/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { defineMessages } from 'react-intl';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { ChartAxisData } from 'src/core/types/chart';
import { IntentMetadata } from 'src/luis/projects/analytics/types/IntentMetadata';
import { SortOptions } from 'src/luis/projects/analytics/types/SortOptions';
import { LuisId } from 'src/luis/types/Resource';

const messages = defineMessages({
    xAxisLabel: {
        id: 'analytics.predictionsPerIntentCard.xAxisLabel',
        defaultMessage: 'Intents (name)'
    }
});

/**
 * Generates the configuration of the distribution chart.
 *
 * @param isNumber The y axis mode.
 * @param sortingType The sort type currently applied.
 * @param utterancesPerIntent A map where the key is the intent id
 * and the value is the metadata of this intent.
 * @returns The generated configurations.
 */
export const getDistributionChartConfig = (
    isNumbers: boolean,
    sortingType: string,
    { lastTrainingDate, utterancesPerIntent }: { lastTrainingDate: Date; utterancesPerIntent: Map<LuisId, IntentMetadata> }
): { xData: ChartAxisData<string>; yData: ChartAxisData<number[]> } => {
    const { localize } = useLocalization();

    const sortedData = lastTrainingDate ? getSortedData(Array.from(utterancesPerIntent.values()), sortingType, isNumbers) : [];

    const xData: ChartAxisData<string> = {
        label: localize(messages.xAxisLabel),
        data: sortedData.map(i => i.intentName)
    };

    const yNumUtterances: number[][] = [[], [], []];
    const yPercentUtterances: number[][] = [[], [], []];

    sortedData.forEach(intent => {
        // Correct utterances.
        yNumUtterances[0].push(intent.numCorrectUtterances);
        // Ambiguous utterances (for bar chart).
        yNumUtterances[1].push(intent.numUnclearUtterances);
        // Incorrect utterances.
        yNumUtterances[2].push(intent.numIncorrectUtterances);

        // Correct utterances percentage.
        yPercentUtterances[0].push(intent.correctAccuracy);
        // Ambiguous and correct utterances (for bar chart) percentage.
        yPercentUtterances[1].push(intent.unclearAccuracy);
        // Incorrect utterances percentage.
        yPercentUtterances[2].push(intent.incorrectAccuracy);
    });

    const yData: ChartAxisData<number[]> = {
        label: 'Utterances',
        data: isNumbers ? yNumUtterances : yPercentUtterances,
        tooltipData: yNumUtterances
    };

    return { xData, yData };
};

/**
 * Sorts data according to the sort type currently applied and the y axis type.
 *
 * @param arr The array to be sorted.
 * @param sort The sort type currently applied.
 * @param isNumber The y axis mode.
 * @returns The Sorted data.
 */
const getSortedData = (arr: IntentMetadata[], sort: string, isNumber: boolean): IntentMetadata[] =>
    arr.sort((a, b) => {
        switch (sort) {
            case SortOptions.allUtterances:
                return a.numUtterances < b.numUtterances ? -1 : 1;
            case SortOptions.unclear:
                const unclearA = isNumber ? a.numUnclearUtterances : a.unclearAccuracy;
                const unclearB = isNumber ? b.numUnclearUtterances : b.unclearAccuracy;

                return unclearA < unclearB ? -1 : 1;
            case SortOptions.incorrect:
                const incorrectA = isNumber ? a.numIncorrectUtterances : a.incorrectAccuracy;
                const incorrectB = isNumber ? b.numIncorrectUtterances : b.incorrectAccuracy;

                return incorrectA < incorrectB ? -1 : 1;
            case SortOptions.intentName:
                return a.intentName.toLocaleLowerCase() < b.intentName.toLocaleLowerCase() ? -1 : 1;
            case SortOptions.correct:
            default:
                const correctA = isNumber ? a.numCorrectUtterances : a.correctAccuracy;
                const correctB = isNumber ? b.numCorrectUtterances : b.correctAccuracy;

                return correctA < correctB ? -1 : 1;
        }
    });
